import { Component, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Minimal, non-reporting error boundary for the public `/c/:token` root
 * (see main.tsx's "Bootstrap correction" comment). Deliberately NOT
 * `Sentry.ErrorBoundary`: this route never initializes Sentry at all (no
 * telemetry for anonymous scanners), so a render throw here -- e.g. from an
 * unexpectedly-shaped card payload -- must fail into the same
 * "couldn't load this card" message `CardStage` already shows for a fetch
 * error, not report anywhere, and above all not white-screen the scanner.
 *
 * Styled with inline styles (matching `ErrorFallback.tsx`'s pattern for the
 * authenticated root) rather than the `bcard-*` classes, so the fallback
 * renders correctly even if this boundary is catching a failure in loading
 * the public page's own lazy chunk (and therefore its CSS never arrived).
 */
export class PublicCardErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            minHeight: "100dvh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#f1f5f7",
            padding: "20px",
            fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "420px",
              padding: "40px 24px",
              borderRadius: "28px",
              background: "#ffffff",
              color: "#0f1b20",
              textAlign: "center",
              boxShadow: "0 2px 6px rgba(15, 27, 32, 0.04)",
              boxSizing: "border-box",
            }}
          >
            <h1 style={{ margin: "0 0 8px", fontSize: "19px", fontWeight: 600 }}>
              Couldn&rsquo;t load this card
            </h1>
            <p style={{ margin: 0, fontSize: "14px", color: "#5f7078" }}>
              Check your connection and try again.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                marginTop: "20px",
                padding: "10px 20px",
                borderRadius: "999px",
                border: "1px solid rgba(15, 27, 32, 0.12)",
                background: "transparent",
                color: "inherit",
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

